import type { Messages } from '../catalogue.en'

/**
 * Tasks: the personal and Patient work list, its editor and reminders.
 *
 * Every refusal the Task API can answer with gets its own sentence, because a
 * generic failure is what makes a clinician reload instead of act. The
 * SMTP acceptance is deliberately distinct from final delivery.
 *
 * The singular count ids carry no `{count}` on purpose: Hebrew says "one task"
 * with a word, not with a digit, exactly as the Patient registry's singular
 * does.
 */
export const tasks: Partial<Messages> = {
  'task.title': 'משימות',
  'task.loading': 'טוען משימות',
  'task.loadFailed': 'לא ניתן לטעון את המשימות.',
  'task.empty': 'אין משימות עדיין',
  'task.emptyDetail': 'משימות שתיצור אתה או עמית יופיעו כאן.',
  'task.filteredEmpty': 'אין משימות התואמות את הסינון',
  'task.filteredEmptyDetail': 'שנה או נקה את הסינון כדי לראות עוד.',
  'task.new': 'משימה חדשה',
  'task.newForEvent': 'משימה חדשה',
  'task.editHeading': 'עריכת משימה',
  'task.editNamed': 'עריכה: {title}',
  'task.save': 'שמור משימה',
  'task.create': 'צור משימה',
  'task.complete': 'סיים',
  'task.completeNamed': 'סיים: {title}',
  'task.cancelTask': 'בטל משימה',
  'task.fieldTitle': 'כותרת',
  'task.details': 'פרטים (רשות)',
  'task.noDetails': 'ללא פרטים',
  'task.dueAt': 'תאריך יעד',
  'task.noDue': 'ללא מועד יעד',
  'task.dueOverdue': 'באיחור',
  'task.dueToday': 'להיום',
  'task.remindAt': 'תזכורת בשעה (רשות)',
  'task.repeat': 'חזרה',
  'task.repeat.none': 'ללא חזרה',
  'task.repeat.daily': 'מדי יום',
  'task.repeat.weekly': 'מדי שבוע',
  'task.repeat.monthly': 'מדי חודש',
  'task.priority': 'עדיפות',
  'task.priority.low': 'נמוכה',
  'task.priority.normal': 'רגילה',
  'task.priority.high': 'גבוהה',
  'task.status.open': 'פתוחה',
  'task.status.inProgress': 'בביצוע',
  'task.status.completed': 'הושלמה',
  'task.status.cancelled': 'בוטלה',
  'task.assignee': 'הרופא האחראי',
  'task.patient': 'מטופל (רשות)',
  'task.scheduleEvent': 'אירוע ביומן (רשות)',
  'task.noPatient': 'ללא מטופל',
  'task.noScheduleEvent': 'ללא אירוע ביומן',
  'task.linkedScheduleEvent': 'אירוע ביומן מקושר · {date}',
  'task.notAnAppointment': 'משימה על אירוע ביומן אינה תור: היא אינה תופסת זמן ואינה שומרת זמינות.',
  'task.unsavedIntent': 'משימה \u2014 שינויים שלא נשמרו',
  'task.notify': 'שלח תזכורות למשימה זו',
  'task.notifyDetail': 'משימה יכולה להתקיים ללא הודעה. הפעל זאת רק כאשר יש להזכיר למישהו.',
  'task.messageText': 'טקסט התזכורת',
  'task.recipients': 'נמענים',
  'task.recipientsHint': 'החזק Ctrl או Cmd כדי לבחור יותר מאחד.',
  'task.titleRequired': 'נדרשת כותרת.',
  'task.assigneeRequired': 'נדרש רופא אחראי אחד.',
  'task.messageIncomplete': 'לתזכורת נדרשים טקסט ולפחות נמען אחד.',
  'task.contextMismatch': 'אירוע היומן שייך למטופל אחר.',
  'task.contextEventOption': '{event}, {patient}',
  'task.staleRevision': 'המשימה שונתה במקום אחר. הטקסט שלך נשמר; טען אותה מחדש כדי לראות את הגרסה הנוכחית לפני השמירה.',
  'task.notActive': 'המשימה הושלמה או בוטלה ולא ניתן עוד לשנותה.',
  'task.rejected': 'המשימה נדחתה כפי שהוזנה. בדוק את השדות ונסה שוב.',
  'task.gone': 'המשימה אינה זמינה עוד.',
  'task.saveFailed': 'לא ניתן לשמור את המשימה. נסה שוב.',
  'task.tableLabel': 'משימות',
  'task.columnTitle': 'משימה',
  'task.columnStatus': 'סטטוס',
  'task.columnPriority': 'עדיפות',
  'task.columnDue': 'יעד',
  'task.columnAssignee': 'אחראי',
  'task.columnPatient': 'מטופל',
  'task.columnActions': 'פעולות',
  'task.filterAssignedToMe': 'שהוקצו לי',
  'task.patientNoMatches': 'אין מטופלים תואמים',
  'task.start': 'התחל עבודה',
  'task.returnToOpen': 'החזר לפתוחה',
  'task.scheduleContext': 'אירוע ביומן',
  'task.resultCountOne': 'משימה אחת',
  'task.resultCountMany': '{count} משימות',
  'task.simulate': 'הרץ סימולציה',
  'task.simulateNamed': 'הרץ סימולציה: {title}',
  'task.simulating': 'מריץ סימולציה…',
  'task.simulateExplains': 'הפעולה מריצה את הערוצים שנבחרו כסימולציה. דבר אינו נשלח ואין פנייה לשום ספק.',
  'task.simulationNotConfigured': 'למשימה זו אין תזכורת מוגדרת, ולכן אין מה לדמות.',
  'task.simulationConflict': 'הסימולציה הזו כבר נרשמה.',
  'task.reminderConfigured': 'הוגדרה תזכורת',
  'task.routingPreferences': 'המסירה נקבעת לפי העדפות התקשורת של כל נמען.',
  'task.attempts': 'ניסיונות תזכורת',
  'task.attemptsNamed': 'ניסיונות תזכורת: {title}',
  'task.attemptsEmpty': 'אין ניסיונות עדיין',
  'task.attemptsFailed': 'לא ניתן לטעון את ניסיונות התזכורת.',
  'task.attemptTrigger.manual': 'ידני',
  'task.attemptTrigger.automatic': 'אוטומטי',
  'task.remindersTitle': 'תזכורות להיום',
  'task.remindersEmpty': 'אין תזכורות ליום זה',
  'task.scheduleCountOne': 'משימה אחת',
  'task.scheduleCountMany': '{count} משימות',
  'task.scheduleRowTasksOne': 'משימה אחת עבור {event}',
  'task.scheduleRowTasksMany': '{count} משימות עבור {event}',
  'task.scheduleUnknown': 'המשימות אינן זמינות',
  'task.scheduleLoadFailed': 'לא ניתן לטעון את התזכורות. התורים שלמעלה אינם מושפעים.',
  'task.openTask': 'פתח את המשימה {title}',
} as const
